import React, { useEffect, useState } from "react";
import AdminNav from "@/components/AdminNav";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Blacklist() {
  const [words, setWords] = useState<string[]>([]);
  const [ips, setIps] = useState<string[]>([]);
  const [uas, setUas] = useState<string[]>([]);

  const [wordFilter, setWordFilter] = useState("");
  const [ipFilter, setIpFilter] = useState("");
  const [uaFilter, setUaFilter] = useState("");

  const [newWord, setNewWord] = useState("");
  const [newIp, setNewIp] = useState("");
  const [newUa, setNewUa] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rw, ri, ru] = await Promise.all([
        fetch("/api/admin/blacklist"),
        fetch("/api/admin/banned-ips"),
        fetch("/api/admin/banned-uas"),
      ]);
      if (rw.ok) {
        const data = await rw.json();
        setWords(data.words ?? []);
      } else setWords([]);

      if (ri.ok) {
        const data = await ri.json();
        setIps(data.ips ?? []);
      } else setIps([]);

      if (ru.ok) {
        const data = await ru.json();
        setUas(data.uas ?? []);
      } else setUas([]);
    } catch (e) {
      setWords([]);
      setIps([]);
      setUas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const addWord = async () => {
    const w = (newWord ?? "").trim();
    if (!w) return alert("Enter a word");
    setLoading(true);
    try {
      const res = await fetch("/api/admin/blacklist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ word: w }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return alert(data?.message ?? "Failed to add word");
      }
      const data = await res.json();
      setWords(data.words ?? []);
      setNewWord("");
    } catch (e) {
      alert("Failed to add word");
    } finally {
      setLoading(false);
    }
  };

  const addIp = async () => {
    const v = (newIp ?? "").trim();
    if (!v) return alert("Enter an IP");
    setLoading(true);
    try {
      const res = await fetch("/api/admin/banned-ips", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ip: v }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return alert(data?.message ?? "Failed to add ip");
      }
      const data = await res.json();
      setIps(data.ips ?? []);
      setNewIp("");
    } catch (e) {
      alert("Failed to add ip");
    } finally {
      setLoading(false);
    }
  };

  const addUa = async () => {
    const v = (newUa ?? "").trim();
    if (!v) return alert("Enter a user agent fragment");
    setLoading(true);
    try {
      const res = await fetch("/api/admin/banned-uas", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ua: v }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return alert(data?.message ?? "Failed to add ua");
      }
      const data = await res.json();
      setUas(data.uas ?? []);
      setNewUa("");
    } catch (e) {
      alert("Failed to add ua");
    } finally {
      setLoading(false);
    }
  };

  const removeWord = async (word: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove "${word}" from blacklist?`)) return;
    setLoading(true);
    try {
      const res = await fetch("/api/admin/blacklist", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ word }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return alert(data?.message ?? "Failed to remove word");
      }
      const data = await res.json();
      setWords(data.words ?? []);
    } catch (e) {
      alert("Failed to remove word");
    } finally {
      setLoading(false);
    }
  };

  const removeIp = async (ip: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove IP ${ip} from ban list?`)) return;
    setLoading(true);
    try {
      const res = await fetch("/api/admin/banned-ips", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ip }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return alert(data?.message ?? "Failed to remove ip");
      }
      const data = await res.json();
      setIps(data.ips ?? []);
    } catch (e) {
      alert("Failed to remove ip");
    } finally {
      setLoading(false);
    }
  };

  const removeUa = async (ua: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove UA fragment "${ua}" from ban list?`)) return;
    setLoading(true);
    try {
      const res = await fetch("/api/admin/banned-uas", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ua }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return alert(data?.message ?? "Failed to remove ua");
      }
      const data = await res.json();
      setUas(data.uas ?? []);
    } catch (e) {
      alert("Failed to remove ua");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AdminNav />
      <div className="min-h-screen p-8 bg-background">
        <div className="max-w-6xl mx-auto mt-6 space-y-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold">Admin — Blacklist & Bans</h1>
              <p className="text-sm text-muted-foreground">Manage words, banned IPs, and banned user agents.</p>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Blocked Words ({words.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 flex gap-2">
                  <Input placeholder="Enter word or phrase" value={newWord} onChange={(e) => setNewWord(e.target.value)} />
                  <Button onClick={addWord} disabled={loading}>Add</Button>
                </div>
                <div className="flex items-center gap-2">
                  <Input placeholder="Filter words" value={wordFilter} onChange={(e) => setWordFilter(e.target.value)} />
                  <Button variant="ghost" onClick={() => setWordFilter("")}>Clear</Button>
                </div>
              </div>
              {loading ? <div>Loading...</div> : null}
              <ul className="space-y-2">
                {words.filter((w) => (wordFilter ? w.toLowerCase().includes(wordFilter.toLowerCase()) : true)).map((w) => (
                  <li key={w} className="flex items-center justify-between border rounded p-2">
                    <div className="truncate">{w}</div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive" onClick={() => removeWord(w)}>Remove</Button>
                    </div>
                  </li>
                ))}
                {words.filter((w) => (wordFilter ? w.toLowerCase().includes(wordFilter.toLowerCase()) : true)).length === 0 && <div className="text-sm text-muted-foreground">No blacklisted words.</div>}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Banned IPs ({ips.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 flex gap-2">
                  <Input placeholder="Enter IP (exact match)" value={newIp} onChange={(e) => setNewIp(e.target.value)} />
                  <Button onClick={addIp} disabled={loading}>Add</Button>
                </div>
                <div className="flex items-center gap-2">
                  <Input placeholder="Filter IPs" value={ipFilter} onChange={(e) => setIpFilter(e.target.value)} />
                  <Button variant="ghost" onClick={() => setIpFilter("")}>Clear</Button>
                </div>
              </div>
              <ul className="space-y-2">
                {ips.filter((i) => (ipFilter ? i.includes(ipFilter) : true)).map((i) => (
                  <li key={i} className="flex items-center justify-between border rounded p-2">
                    <div className="truncate">{i}</div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive" onClick={() => removeIp(i)}>Remove</Button>
                    </div>
                  </li>
                ))}
                {ips.filter((i) => (ipFilter ? i.includes(ipFilter) : true)).length === 0 && <div className="text-sm text-muted-foreground">No banned IPs.</div>}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Banned User Agents ({uas.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 flex gap-2">
                  <Input placeholder="Enter UA fragment (case-insensitive)" value={newUa} onChange={(e) => setNewUa(e.target.value)} />
                  <Button onClick={addUa} disabled={loading}>Add</Button>
                </div>
                <div className="flex items-center gap-2">
                  <Input placeholder="Filter UAs" value={uaFilter} onChange={(e) => setUaFilter(e.target.value)} />
                  <Button variant="ghost" onClick={() => setUaFilter("")}>Clear</Button>
                </div>
              </div>
              <ul className="space-y-2">
                {uas.filter((u) => (uaFilter ? u.toLowerCase().includes(uaFilter.toLowerCase()) : true)).map((u) => (
                  <li key={u} className="flex items-center justify-between border rounded p-2">
                    <div className="truncate">{u}</div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive" onClick={() => removeUa(u)}>Remove</Button>
                    </div>
                  </li>
                ))}
                {uas.filter((u) => (uaFilter ? u.toLowerCase().includes(uaFilter.toLowerCase()) : true)).length === 0 && <div className="text-sm text-muted-foreground">No banned user agents.</div>}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
